import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Button, Grid, Text, Title } from '@factorialco/deprecated-design-system'
import {
  ConfirmImportProjectsDocument,
  ConfirmImportProjectsMutation,
  ProjectManagementImportProjectsPageDocument,
} from 'generated/resources/graphql'

import WithMultiSelectState, {
  WithMultiSelectStateChildrenProps,
} from 'modules/core/components/WithMultiSelectState'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ImportDropzone from 'modules/project_management/components/Dropzone'
import ImportProjectsConfirmModal from 'modules/project_management/components/modals/ImportProjectsConfirmModal'
import ImportProjectsTable from 'modules/project_management/components/tables/ImportProjectsTable'
import { LegalEntity } from 'modules/project_management/components/tables/ImportProjectsTable/_Table/_Body/CellContent'
import { DEFAULT_CURRENCY } from 'modules/project_management/lib/constants'
import { ProjectsToImport } from 'modules/project_management/lib/types'

const ImportProjectsDetailsPage = () => {
  const [isImportConfirmModalOpen, setIsImportConfirmModalOpen] = React.useState<boolean>(false)

  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { state } = useLocation()
  const { company } = useCurrentSession()

  const {
    data: page,
    loading,
    error,
  } = useQuery(ProjectManagementImportProjectsPageDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const hasImportProjectsEnabled =
    !!page?.companies.featureAvailabilitiesConnection.nodes[0]?.available

  const [confirmImportProjects] = useMutation(ConfirmImportProjectsDocument, {
    extractorFn: (data: ConfirmImportProjectsMutation) =>
      data.projectManagementImporterMutations.confirmProjectmanagementimporterProjectsImport,
  })

  const legalEntities: LegalEntity[] = company.legalEntities || []
  const defaultCurrency = company.currency || DEFAULT_CURRENCY

  if (!hasImportProjectsEnabled) return null

  const { token, projects } = state || { token: '', projects: [] }

  const parsedProjects = projects.map((item: string) => JSON.parse(item))

  const projectsReady = parsedProjects.filter(
    (project: ProjectsToImport) => Object.keys(project.errors).length === 0
  )
  const projectsErrored = parsedProjects.filter(
    (project: ProjectsToImport) => Object.keys(project.errors).length > 0
  )

  const onDropFile = (files: File[]) => {
    navigate(navigation.projects.list.import.fullPath(), {
      state: { fileDroppedFromDetailsPage: files },
    })
  }

  const openModal = () => {
    track(trackingEvents.projectManagement.importProjects.confirmImportProjectsStart)
    setIsImportConfirmModalOpen(true)
  }

  const onCloseModal = () => {
    track(trackingEvents.projectManagement.importProjects.cancelConfirmImportProjects)
    setIsImportConfirmModalOpen(false)
  }

  const onSubmit = async (selectedProjects: ProjectsToImport[]) => {
    track(trackingEvents.projectManagement.importProjects.confirmImportProjectsSubmit)
    const projectNames = selectedProjects.map((item) => item.project_name)

    const result = await confirmImportProjects({
      variables: {
        selectedProjects: projectNames,
        token,
      },
      optimisticResponse: {
        projectManagementImporterMutations: {
          confirmProjectmanagementimporterProjectsImport: {
            errors: [],
            projectsImport: {
              projects: projectNames,
              token: token,
            },
            __typename: 'ConfirmProjectmanagementimporterProjectsImportPayload',
          },
        },
      },
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      track(trackingEvents.projectManagement.importProjects.confirmImportProjectsSuccess, {
        projects_to_import: projectNames.length,
      })
      navigate(navigation.projects.list.fullPath())
      app.setFlash({
        type: 'correct',
        message: i18n.t('project_management.toasts.import_projects_success'),
      })
    }
  }

  return (
    <ResponsivePage.Body showHeader={false} loading={loading} error={error}>
      <WithMultiSelectState items={projectsReady} initialSelection={projectsReady}>
        {({
          onDeselectAll,
          onSelectAll,
          onToggleSelectedItem,
          items,
          selected,
        }: WithMultiSelectStateChildrenProps<ProjectsToImport>) => (
          <>
            <Grid variant="fixed">
              <Box gridColSpan={{ xs: 12, s: 6 }}>
                <Title
                  variant="500"
                  illustration="document-edit"
                  title={i18n.t('project_management.imports.title')}
                  description={i18n.t('project_management.imports.description_page')}
                />
              </Box>
              <Box gridColSpan={{ xxs: 12, s: 6 }}>
                <ImportDropzone
                  label={i18n.tView('project_management.imports.drag_drop_placeholder', {
                    browse: (
                      <Text.Normal color="viridian1000" weight="medium">
                        {i18n.t('project_management.imports.browse')}{' '}
                      </Text.Normal>
                    ),
                  })}
                  onDropFile={onDropFile}
                />
              </Box>
            </Grid>
            <Box position="relative" style={{ width: '100%' }} paddingTop="s40">
              <Box style={{ position: 'absolute', right: '0px' }}>
                <Button onClick={openModal} disabled={!selected.length}>
                  {i18n.t('project_management.imports.import_button', {
                    count: selected.length,
                  })}
                </Button>
              </Box>
              <ImportProjectsTable
                projectsReady={items}
                projectsErrored={projectsErrored}
                selected={selected}
                onToggleSelectedItem={onToggleSelectedItem}
                onDeselectAll={onDeselectAll}
                onSelectAll={onSelectAll}
                legalEntities={legalEntities}
                defaultCurrency={defaultCurrency}
              />
            </Box>
            {isImportConfirmModalOpen && (
              <ImportProjectsConfirmModal
                onClose={onCloseModal}
                selectedProjects={selected}
                onSubmit={onSubmit}
              />
            )}
          </>
        )}
      </WithMultiSelectState>
    </ResponsivePage.Body>
  )
}

export default ImportProjectsDetailsPage
